import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import CloseIcon from '@/shared/icons/CloseIcon'
import ButtonIcon from '@/shared/components/UI/ButtonIcon'
import clsx from 'clsx'

/**
 * Modal component with custom design
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} title - Modal title
 * @param {string} description - Optional subtitle rendered below the title
 * @param {React.ReactNode} children - Modal content
 * @param {string} className - Additional classes for the modal panel
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  description,
  children,
  className = '',
  titleClassName = '',
  subtitleClassName = '',
  headerClassName = '',
  closeBackdrop = true,
  /** Center title + subtitle; close button stays top-right */
  centeredHeader = false,
  /** Classes for the scrollable body wrapper (default padding) */
  bodyClassName = '',
  /** Extra classes for the close control (e.g. match custom header padding) */
  closeButtonClassName = '',
  /** Optional backdrop classes (e.g. longer fade with panel) */
  backdropClassName = '',
}) => {
  const handleClose = () => {
    if (!closeBackdrop) return
    onClose?.()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="relative z-50"
      transition
    >
      {/* Backdrop */}
      <DialogBackdrop
        transition
        className={clsx(
          'fixed inset-0 animate-modal-backdrop-in transition duration-300 ease-out data-[closed]:opacity-0',
          backdropClassName
        )}
        style={{ backgroundColor: 'var(--backdrop-overlay)' }}
        onClick={handleClose}
      />

      {/* Modal container */}
      <div className="fixed inset-0 flex items-center justify-center p-0 md:p-v3-md">
        <DialogPanel
          transition
          className={`
            flex max-h-[100dvh] min-h-0 flex-col
            h-auto w-full overflow-hidden
            rounded-none
            border
            bg-white
            shadow-v3-card
            border-v3-card-border
            md:max-h-[min(90dvh,920px)]
            md:w-[800px]
            md:rounded-v3-md
            animate-modal-panel-in transform-gpu
            transition duration-200 ease-out
            data-[closed]:opacity-0 data-[closed]:scale-95
            ${className}
          `}

        >
          {/* Header — close is always absolute top-right so it never vertically centers with title */}
          <div
            className={clsx(
              'flex px-v3-lg py-v3-md justify-between self-stretch border-b border-v3-card-border',
              (subtitle || description) ? 'items-start' : 'items-center',
              centeredHeader ? 'justify-center text-center' : '',
              headerClassName
            )}
          >
            <div
              className={clsx(
                'flex min-w-0 flex-1 flex-col gap-v3-xs pr-11',
                centeredHeader && 'max-w-[min(720px,100%)] items-center px-v3-xxl mb-6'
              )}
            >
              <h2
                className={clsx(
                  'font-bold text-v3-black-max',
                  centeredHeader ? 'text-xl leading-tight' : 'text-v3-h5',
                  titleClassName
                )}
              >
                {title}
              </h2>
              {subtitle && (
                <p
                  className={clsx(
                    centeredHeader ? 'text-sm leading-snug' : 'text-v3-h10',
                    subtitleClassName,
                    !subtitleClassName &&
                    (centeredHeader ? 'text-v3-black-secondary' : 'text-v3-black-max')
                  )}
                >
                  {subtitle}
                </p>
              )}
              {description && (
                <p className="text-v3-h9 text-v3-black-secondary">{description}</p>
              )}
            </div>
            <ButtonIcon
              onClick={onClose}
              aria-label="Close modal"
              size="sm"
              icon={<CloseIcon />}
              className={clsx(
                'absolute right-v3-lg top-v3-md z-10 shrink-0',
                closeButtonClassName
              )}
            />
          </div>

          {/* Main content — flex-1 + min-h-0 so tall bodies scroll inside max-h panel (no full-viewport flash) */}
          <div
            className={clsx(
              'flex min-h-0 flex-1 flex-col justify-start overflow-y-auto overscroll-contain px-v3-lg pb-v3-lg pt-v3-md',
              bodyClassName
            )}
          >
            {children}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default Modal
